import { db } from "../firebase";

/**
 * Builds role summary for a specific player by:
 * 1. Fetching all reports for the player
 * 2. Counting roles from reports
 * 3. Fetching role adjustments
 * 4. Combining live counts with adjustments
 * 5. Saving to player-role-summaries collection
 * 
 * @param {string} playerId - The player profile ID
 * @returns {Promise<Object>} Final role counts object
 */
export const buildPlayerRoleSummary = async (playerId) => {
  try {
    console.log(`Building role summary for player: ${playerId}`);
    
    // Try different possible field names for player profile ID
    let reportsSnapshot;
    
    // First try: playerProfile (direct reference) - based on your sample structure
    reportsSnapshot = await db.collection("reports")
      .where("playerProfile", "==", playerId)
      .get();
    
    if (reportsSnapshot.empty) {
      // Second try: playerProfile.id
      reportsSnapshot = await db.collection("reports")
        .where("playerProfile.id", "==", playerId)
        .get();
    }
    
    if (reportsSnapshot.empty) {
      // Third try: playerId field
      reportsSnapshot = await db.collection("reports")
        .where("playerId", "==", playerId)
        .get();
    }
    
    console.log(`Found ${reportsSnapshot.size} reports for player ${playerId}`);
    
    // Count roles from reports
    const roleCounts = {};
    reportsSnapshot.docs.forEach((doc, index) => {
      const report = doc.data();
      
      // Check for roles in typeSpecificFields.roles (Scouting reports)
      if (report.typeSpecificFields && report.typeSpecificFields.roles && Array.isArray(report.typeSpecificFields.roles)) {
        report.typeSpecificFields.roles.forEach(role => {
          // Filter out empty, null, or undefined role names
          if (role && typeof role === 'string' && role.trim() !== '') {
            roleCounts[role] = (roleCounts[role] || 0) + 1;
          }
        });
      }
      // Check for roles directly in report.roles (fallback)
      else if (report.roles && Array.isArray(report.roles)) {
        report.roles.forEach(role => {
          if (role && typeof role === 'string' && role.trim() !== '') {
            roleCounts[role] = (roleCounts[role] || 0) + 1;
          }
        });
      }
    });
    
    // Clean up any empty keys that might have been created
    Object.keys(roleCounts).forEach(key => {
      if (!key || typeof key !== 'string' || key.trim() === '') {
        delete roleCounts[key];
      }
    });
    
    // Get role adjustments for this player
    const adjustmentsSnapshot = await db.collection("role-adjustments")
      .where("playerID", "==", playerId)
      .get();
    
    const roleAdjustments = {};
    adjustmentsSnapshot.docs.forEach((doc) => {
      const adjustment = doc.data();
      
      // Use 'role' field and 'count' field
      if (adjustment.role && adjustment.count !== undefined) {
        const roleName = adjustment.role;
        const adjustmentValue = adjustment.count;
        
        // Aggregate adjustments for the same role
        roleAdjustments[roleName] = (roleAdjustments[roleName] || 0) + adjustmentValue;
      }
    });
    
    // Calculate final counts
    const finalCounts = {};
    
    Object.keys(roleCounts).forEach(role => {
      const liveCount = roleCounts[role] || 0;
      const adjustment = roleAdjustments[role] || 0;
      
      finalCounts[role] = {
        liveCount,
        adjustment,
        finalCount: liveCount + adjustment
      };
    });
    
    // Add roles that only have adjustments
    Object.keys(roleAdjustments).forEach(role => {
      if (!finalCounts[role]) {
        finalCounts[role] = {
          liveCount: 0,
          adjustment: roleAdjustments[role],
          finalCount: roleAdjustments[role]
        };
      }
    });
    
    // Save to player-role-summaries collection
    await db.collection("player-role-summaries").doc(playerId).set({
      roleCounts: finalCounts,
      lastUpdated: new Date(),
      playerId: playerId
    });
    
    console.log(`Successfully saved role summary to database for player ${playerId}`);
    
    return finalCounts;
  } catch (error) {
    console.error(`Error building role summary for player ${playerId}:`, error);
    throw error;
  }
};

